import { Divider, Grid, Paper } from '@mui/material';
import type { Currency, Plan } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import React, { useMemo } from 'react';
import { formatDate } from '../../../../utils/date';
import { getPlanDurationLabel } from '../../../plans/utils';
import { ContractInfoBlock } from './ContractInfoBlock';
import { ValueDisplay } from './ValueDisplay';
import { formatRefNumber } from '../../utils/formatRefNumber';
import { calculateEstimatedPlanValue } from '../../../../utils/contract/calculateEstimatedPlanValue';

type ContractSummaryDetailsProps = {
  plan: Plan;
  currency: Currency;
  refNumber: string;
  startDate?: Date;
  endDate?: Date;
  revenue?: number;
  customersCount?: number;
  isPlan?: boolean;
  effectiveAt?: Date;
};

export const ContractSummaryDetails: React.FC<ContractSummaryDetailsProps> = ({
  plan,
  currency,
  refNumber,
  startDate,
  revenue,
  customersCount,
  endDate,
  isPlan = false,
}) => {
  const estimatedContractValue = useMemo(() => {
    return calculateEstimatedPlanValue(plan);
  }, [plan]);

  return (
    <Grid container spacing={2}>
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Paper
          sx={{
            width: '100%',
            gap: '16px',
            height: '92px',
            minWidth: '380px',
            borderRadius: '8px',
            border: '1px',
            padding: '24px',
            display: 'flex',
            wordBreak: 'break-word',
            alignItems: 'stretch',
          }}
          elevation={1}
        >
          <ContractInfoBlock label="Ref Number" value={formatRefNumber(refNumber)} />

          {isNotNullOrUndefined(startDate) && (
            <ContractInfoBlock label="Start Date" value={formatDate(startDate)} />
          )}

          {isNotNullOrUndefined(customersCount) && (
            <ContractInfoBlock label="Customers" value={customersCount.toString()} />
          )}

          {isNotNullOrUndefined(endDate) ? (
            <ContractInfoBlock label="End Date" value={formatDate(endDate)} />
          ) : (
            <ContractInfoBlock label="Duration" value={getPlanDurationLabel(plan.billingData.duration)} />
          )}

          <ContractInfoBlock label="Created At" value={formatDate(plan.createdAt)} />
        </Paper>
      </Grid>
      <Grid
        size={{
          xs: 12,
          md: 6
        }}>
        <Paper
          sx={{
            width: '100%',
            gap: '16px',
            height: '92px',
            minWidth: '380px',
            borderRadius: '8px',
            border: '1px',
            padding: '24px 64px ',
            wordBreak: 'break-word',
            display: 'flex',
            justifyContent: 'space-around',
          }}
          elevation={1}
        >
          {!isPlan && (
            <>
              <ValueDisplay label="Revenue" currency={currency} value={revenue ?? 0} />
              <Divider orientation="vertical" flexItem />
            </>
          )}
          <ValueDisplay
            label={`${!isPlan ? 'Contract' : ''} Value Estimation`}
            currency={currency}
            value={estimatedContractValue ?? 0}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};
